/**
 * EntityStructure - port from `1116/omega-club/dashboard/src/components/EntityStructure.jsx`.
 * Reads from `useTenantData(slug).llcEntities`.
 *
 * Per CHAP-82 (Phase 1C #48).
 *
 * Parity notes vs omega-club:
 * - omega-club's "Capital structure scenarios" section (LLC-heavy vs
 *   Brother Bonds, added in CHAP-252) reads from `capitalStructureScenarios`
 *   which isn't in TenantConfig. Section omitted until a follow-up adds
 *   the schema field.
 * - omega-club's "Investment Opportunity by LLC" bottom card has fields
 *   (capitalNeeded / avgReturnYr10 / irrYr20 / pitch) not in chapter-build's
 *   `LlcEntity` shape. Omitted; the per-LLC card already shows cost /
 *   revenue / NOI / beds / sqft.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { formatCurrency, formatNumber } from '../lib/formatters.js';

export default function EntityStructure() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  const totals = useMemo(() => {
    if (!data) return null;
    const t = { cost: 0, revenue: 0, noi: 0, beds: 0, sqft: 0 };
    for (const llc of data.llcEntities) {
      t.cost += llc.cost ?? 0;
      t.revenue += llc.revenueYr1 ?? 0;
      t.noi += llc.noiYr1 ?? 0;
      t.beds += llc.beds ?? 0;
      t.sqft += llc.sqft ?? 0;
    }
    return t;
  }, [data]);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data || !totals) return <PageState label="No data" />;
  if (data.llcEntities.length === 0) {
    return <PageState label="No LLCs seeded for this tenant." />;
  }

  const noiMarginPct = totals.revenue > 0 ? Math.round((totals.noi / totals.revenue) * 100) : 0;
  const llcs = [...data.llcEntities].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0">
      {/* Hero */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Entity Structure</h2>
            <p className="text-neutral text-sm sm:text-base mt-1">
              {llcs.length}-LLC ownership model for {data.tenant.displayName}
            </p>
          </div>
          {totals.noi > 0 && (
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-xs uppercase tracking-wide text-neutral font-bold">Combined NOI</span>
              <span className="text-positive text-2xl sm:text-3xl font-bold">{formatCurrency(totals.noi)}</span>
              <span className="text-xs text-neutral">{noiMarginPct}% margin on {formatCurrency(totals.revenue)} revenue</span>
            </div>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <SummaryTile label="Total Cost"    value={formatCurrency(totals.cost)} />
        <SummaryTile label="Total Revenue" value={formatCurrency(totals.revenue)} />
        <SummaryTile label="Total NOI"     value={formatCurrency(totals.noi)} positive />
        <SummaryTile label="Total Beds"    value={String(totals.beds)} />
        <SummaryTile label="Total SF"      value={formatNumber(totals.sqft)} colSpanFull />
      </div>

      {/* LLC cards */}
      <div className="space-y-3 sm:space-y-4">
        {llcs.map((llc) => {
          const costPct = totals.cost > 0 ? Math.round(((llc.cost ?? 0) / totals.cost) * 100) : 0;
          const revPct = totals.revenue > 0 ? Math.round(((llc.revenueYr1 ?? 0) / totals.revenue) * 100) : 0;
          const margin = llc.revenueYr1 && llc.noiYr1 ? Math.round((llc.noiYr1 / llc.revenueYr1) * 100) : 0;
          return (
            <div key={llc.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3 sm:mb-4 flex-wrap gap-2 sm:gap-3 min-w-0">
                <div className="min-w-0">
                  <h3 className="text-navy text-base sm:text-lg font-bold truncate">{llc.name}</h3>
                  {llc.subtitle && <p className="text-orange font-medium text-xs sm:text-sm">{llc.subtitle}</p>}
                </div>
                <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
                  {(llc.beds ?? 0) > 0 && (
                    <span className="text-xs bg-navy-dark text-orange px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-medium">
                      {llc.beds} beds
                    </span>
                  )}
                  {llc.sqft != null && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-medium">
                      {formatNumber(llc.sqft)} sf
                    </span>
                  )}
                </div>
              </div>

              {llc.description && (
                <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">{llc.description}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-100">
                <Cell label="Cost"    value={formatCurrency(llc.cost)}        sub={`${costPct}% of total`} />
                <Cell label="Revenue" value={formatCurrency(llc.revenueYr1)} sub={`${revPct}% of total`} />
                <Cell label="NOI"     value={formatCurrency(llc.noiYr1)}      sub={`${margin}% margin`} positive />
                <Cell
                  label="Cost / SF"
                  value={llc.cost && llc.sqft ? `$${Math.round(llc.cost / llc.sqft)}` : 'n/a'}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Structure note */}
      <div className="bg-navy-dark/5 rounded-xl p-3 sm:p-4 border border-navy-dark/10">
        <p className="text-xs sm:text-sm text-navy">
          <span className="font-semibold">Structure:</span> Each LLC operates independently with its own revenue streams
          and cost basis. The {llcs.length}-LLC model lets different investor groups participate in specific aspects of
          the project while maintaining clean separation of assets and liabilities.
        </p>
      </div>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  positive,
  colSpanFull,
}: {
  label: string;
  value: string;
  positive?: boolean;
  colSpanFull?: boolean;
}) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 text-center min-w-0 ${
        colSpanFull ? 'col-span-2 sm:col-span-1' : ''
      }`}
    >
      <p className="text-neutral text-xs uppercase tracking-wider">{label}</p>
      <p className={`text-base sm:text-xl font-bold mt-1 truncate ${positive ? 'text-positive' : 'text-navy'}`}>
        {value}
      </p>
    </div>
  );
}

function Cell({ label, value, sub, positive }: { label: string; value: string; sub?: string; positive?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-neutral text-xs uppercase tracking-wider">{label}</p>
      <p className={`font-bold text-sm sm:text-lg truncate ${positive ? 'text-positive' : 'text-navy'}`}>{value}</p>
      {sub && <p className="text-neutral text-xs">{sub}</p>}
    </div>
  );
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
